import type { CartOutputDto } from "@sticker-giant/sg-frontend-common";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { trackCouponApplied } from "@/lib/analytics/segment";
import { getCustomerInfo } from "@/lib/cart";
import { CART_ACTIVE_FILTER } from "@/queries/useCart";
import { useGlobalStore } from "@/stores/useGlobalStore";
import { useServicesStore } from "@/stores/useServicesStore";

export const applyCouponMutationKey = ["applyCoupon"] as const;

interface ApplyCouponInput {
  code: string;
}

export function useApplyCoupon() {
  const cartClient = useServicesStore(state => state.cartClient);
  const cartId = useGlobalStore(state => state.cartId);
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: applyCouponMutationKey,
    mutationFn: async ({ code }: ApplyCouponInput): Promise<CartOutputDto> => {
      if (!cartClient) throw new Error("Cart client is not available.");
      // A null cart id is valid state: /coupon finds the customer's open cart, or creates
      // one, so a promo link works before the visitor has added anything.
      return await cartClient.postCouponCodeNullCartSafe(code, cartId ?? null, getCustomerInfo());
    },
    onSuccess: (cart, { code }) => {
      queryClient.setQueriesData<CartOutputDto | null>(CART_ACTIVE_FILTER, cart);
      useGlobalStore.getState().setCartId(cart.id);
      trackCouponApplied({
        cartId: cart.id,
        couponCode: cart.couponCode ?? code,
        couponStatus: cart.couponStatus,
        pricing: cart.pricing,
      });
    },
  });
}
